import { useCallback, useState } from "react";
import { TimeframeObject } from "./TimeframeObject";
import { TimeframeProps } from "./Timeframe";
import { TimeframeUtils } from "./TimeframeUtils";
import { TimeframeOptions } from "./TimeframeOptions";
import { TimeframeUnit } from "./TimeframeUnit";
import { useTimeframeState } from "./useTimeframeState";
import { resolveDefaultTimeframeOptions } from "./resolveDefaultTimeframeOptions";

export type UseTimeframeInput = Partial<TimeframeObject> &
  Partial<TimeframeOptions>;

export const useTimeframe = (input: UseTimeframeInput): TimeframeProps => {
  const { start, end, ...partialOptions } = input;
  const [options, setOptions] = useState<TimeframeOptions>(
    resolveDefaultTimeframeOptions(partialOptions)
  );
  const [timeframe, setTimeframe] = useTimeframeState(options);

  const handleTimeframeChange = useCallback(
    (newTimeframe: TimeframeObject) => {
      setTimeframe(newTimeframe);
    },
    [setTimeframe]
  );

  const handleUnitChange = useCallback(
    (newUnit: TimeframeUnit) => {
      const newTimeframe = TimeframeUtils.transformTimeframeToUnit({
        timeframe: timeframe,
        options: options,
        newUnit: newUnit,
        oldUnit: options.unit,
      });

      setOptions((prevState) => {
        return { ...prevState, unit: newUnit };
      });

      setTimeframe(newTimeframe);
    },
    [timeframe, options, setTimeframe]
  );

  const handleYearChange = useCallback(
    (newYear: number) => {
      setTimeframe(
        TimeframeUtils.expandWithinUnit(
          TimeframeUtils.changeYear(timeframe, newYear),
          options
        )
      );
    },
    [timeframe, options, setTimeframe]
  );

  const handleMonthChange = useCallback(
    (newMonth: number) => {
      setTimeframe(
        TimeframeUtils.expandWithinUnit(
          TimeframeUtils.changeMonth(timeframe, newMonth),
          options
        )
      );
    },
    [timeframe, options, setTimeframe]
  );

  const handlePrevious = useCallback(() => {
    setTimeframe(TimeframeUtils.resolvePreviousTimeframe(timeframe, options));
  }, [timeframe, options, setTimeframe]);

  const handleNext = useCallback(() => {
    setTimeframe(TimeframeUtils.resolveNextTimeframe(timeframe, options));
  }, [timeframe, options, setTimeframe]);

  const handleNow = useCallback(() => {
    setTimeframe(TimeframeUtils.resolveNowTimeframe(options));
  }, [options, setTimeframe]);

  return {
    timeframe: timeframe,
    timeframeUnit: options.unit,
    onNextTimeframe: handleNext,
    onNowTimeframe: handleNow,
    onPreviousTimeframe: handlePrevious,
    onTimeframeChange: handleTimeframeChange,
    onUnitChange: handleUnitChange,
    onYearChange: handleYearChange,
    onMonthChange: handleMonthChange,
    options: options,
  };
};
